/**
 * Windows 动态指针 .ani（RIFF/ACON）解析器
 *
 * 浏览器原生只认 .cur/.png 之类，对 .ani 支持很差（各平台还不一致），
 * 所以这里把 .ani 拆成一张张 canvas，再用一个跟着鼠标跑的小画布自己播动画。
 *
 * 支持：帧数据是「BMP(DIB)」的 CUR/ICO（32/24/8/4/1 位色 + AND 掩码），
 *       以及内嵌 PNG 的帧（用 Image 异步解）。
 * 返回：{ width, height, hotspot, frames: [canvas], rates, steps }
 */

function tagAt(dv, o) {
  return String.fromCharCode(dv.getUint8(o), dv.getUint8(o + 1), dv.getUint8(o + 2), dv.getUint8(o + 3));
}

/** 解一张 CUR / ICO，挑最大的那一张图 */
async function decodeIcon(buffer, offset, size) {
  const dv = new DataView(buffer, offset, size);
  const count = dv.getUint16(4, true);
  if (!count) throw new Error('图标里没有图像');

  let best = null;
  for (let i = 0; i < count; i++) {
    const e = 6 + i * 16;
    const w = dv.getUint8(e) || 256;
    const h = dv.getUint8(e + 1) || 256;
    const cand = {
      w: w,
      h: h,
      hotspot: { x: dv.getUint16(e + 4, true), y: dv.getUint16(e + 6, true) },
      bytes: dv.getUint32(e + 8, true),
      off: dv.getUint32(e + 12, true)
    };
    if (!best || cand.w * cand.h > best.w * best.h) best = cand;
  }
  if (!best || best.off + best.bytes > size) throw new Error('图标数据结构不对');

  const absOff = offset + best.off;
  const probe = new Uint8Array(buffer, absOff, 4);
  const canvas = document.createElement('canvas');
  canvas.width = best.w;
  canvas.height = best.h;
  const ctx = canvas.getContext('2d');

  // ① 内嵌 PNG
  if (probe[0] === 0x89 && probe[1] === 0x50) {
    const blob = new Blob([new Uint8Array(buffer, absOff, best.bytes)], { type: 'image/png' });
    const url = URL.createObjectURL(blob);
    try {
      const img = await new Promise((res, rej) => {
        const im = new Image();
        im.onload = () => res(im);
        im.onerror = () => rej(new Error('PNG 帧解码失败'));
        im.src = url;
      });
      ctx.drawImage(img, 0, 0);
    } finally {
      URL.revokeObjectURL(url);
    }
    return { canvas: canvas, hotspot: best.hotspot, width: best.w, height: best.h };
  }

  // ② BMP(DIB)
  const h = new DataView(buffer, absOff, best.bytes);
  const biSize = h.getUint32(0, true);
  const biWidth = h.getInt32(4, true);
  let biHeight = h.getInt32(8, true);
  const biBitCount = h.getUint16(14, true);
  const compression = h.getUint32(16, true);
  if (compression !== 0) throw new Error('不支持的图标压缩格式 ' + compression);

  const width = Math.abs(biWidth) || best.w;
  const height = Math.abs(biHeight) / 2 || best.h;   // DIB 高度是「图像 + 掩码」两倍
  const bottomUp = biHeight > 0;
  const paletteBytes = biBitCount <= 8 ? (h.getUint32(32, true) || (1 << biBitCount)) * 4 : 0;
  const dataOff = absOff + biSize + paletteBytes;
  const xorStride = Math.ceil((biBitCount * width) / 32) * 4;
  const andStride = Math.ceil(width / 32) * 4;
  const andOff = dataOff + xorStride * height;
  const db = new DataView(buffer);
  const img = ctx.createImageData(width, height);

  // 先看 alpha 通道是不是全 0 —— 有些图标不带 alpha，只能用 AND 掩码
  let alphaSum = 0;
  if (biBitCount === 32) {
    for (let y = 0; y < height; y++) {
      const srcY = bottomUp ? (height - 1 - y) : y;
      for (let x = 0; x < width; x++) alphaSum += db.getUint8(dataOff + srcY * xorStride + x * 4 + 3);
    }
  }

  for (let y = 0; y < height; y++) {
    const srcY = bottomUp ? (height - 1 - y) : y;
    for (let x = 0; x < width; x++) {
      let r = 0, g = 0, b = 0, a = 255;
      if (biBitCount === 32) {
        const o = dataOff + srcY * xorStride + x * 4;
        b = db.getUint8(o); g = db.getUint8(o + 1); r = db.getUint8(o + 2); a = db.getUint8(o + 3);
        if (!alphaSum) a = 255;
      } else if (biBitCount === 24) {
        const o = dataOff + srcY * xorStride + x * 3;
        b = db.getUint8(o); g = db.getUint8(o + 1); r = db.getUint8(o + 2);
      } else {
        let idx = 0;
        if (biBitCount === 8) idx = db.getUint8(dataOff + srcY * xorStride + x);
        else if (biBitCount === 4) {
          const o = dataOff + srcY * xorStride + (x >> 1);
          idx = (x % 2) ? (db.getUint8(o) & 0x0f) : (db.getUint8(o) >> 4);
        } else if (biBitCount === 1) {
          const o = dataOff + srcY * xorStride + (x >> 3);
          idx = (db.getUint8(o) >> (7 - (x & 7))) & 1;
        }
        const po = absOff + biSize + idx * 4;
        b = db.getUint8(po); g = db.getUint8(po + 1); r = db.getUint8(po + 2);
      }
      // AND 掩码：置 1 表示透明
      if (biBitCount !== 32 || !alphaSum) {
        const bit = (db.getUint8(andOff + srcY * andStride + (x >> 3)) >> (7 - (x & 7))) & 1;
        if (bit) a = 0;
      }
      const d = (y * width + x) * 4;
      img.data[d] = r; img.data[d + 1] = g; img.data[d + 2] = b; img.data[d + 3] = a;
    }
  }
  ctx.putImageData(img, 0, 0);
  return { canvas: canvas, hotspot: best.hotspot, width: width, height: height };
}

/** 解析 .ani，返回可直接播放的一组帧 */
export async function parseAni(buffer) {
  const dv = new DataView(buffer);
  if (tagAt(dv, 0) !== 'RIFF' || tagAt(dv, 8) !== 'ACON') throw new Error('不是 ANI 文件');
  // 有些 ani 的 RIFF 尺寸字段比实际文件大几个字节，必须夹一下，
  // 否则循环会读到缓冲区外，抛「Offset is outside the bounds of the DataView」
  const riffEnd = Math.min(8 + dv.getUint32(4, true), dv.byteLength);

  let anih = null;
  const rates = [];
  const steps = [];
  const icons = [];

  let p = 12;
  while (p + 8 <= riffEnd) {
    const id = tagAt(dv, p);
    const size = dv.getUint32(p + 4, true);
    const data = p + 8;
    if (id === 'anih') {
      anih = {
        cFrames: dv.getUint32(data + 4, true),
        cSteps: dv.getUint32(data + 8, true),
        cx: dv.getUint32(data + 12, true),
        cy: dv.getUint32(data + 16, true),
        bpp: dv.getUint32(data + 20, true)
      };
    } else if (id === 'rate') {
      for (let i = 0; i + 4 <= size; i += 4) rates.push(dv.getUint32(data + i, true));
    } else if (id === 'seq ') {
      for (let i = 0; i + 4 <= size; i += 4) steps.push(dv.getUint32(data + i, true));
    } else if (id === 'LIST' && tagAt(dv, data) === 'fram') {
      let q = data + 4;
      const listEnd = Math.min(data + size, dv.byteLength);
      while (q + 8 <= listEnd) {
        const cid = tagAt(dv, q);
        const csize = dv.getUint32(q + 4, true);
        if (cid === 'icon') icons.push({ offset: q + 8, size: csize });
        q += 8 + csize + (csize % 2);
      }
    }
    p += 8 + size + (size % 2);
  }

  if (!icons.length) throw new Error('这个 ANI 里没有图像帧');

  const decoded = [];
  for (const ic of icons) decoded.push(await decodeIcon(buffer, ic.offset, ic.size));

  const frames = decoded.map((d) => d.canvas);
  const first = decoded[0];
  const n = frames.length;
  return {
    width: (anih && anih.cx) || first.width || 32,
    height: (anih && anih.cy) || first.height || 32,
    hotspot: first.hotspot || { x: 0, y: 0 },
    frames: frames,
    // rate 单位是 1/60 秒；没有 rate 就给个默认 6（≈100ms）
    rates: rates.length === n ? rates : new Array(n).fill(6),
    steps: steps.length ? steps : frames.map((_, i) => i)
  };
}

/** 量出一张 canvas 里「真正画了东西」的矩形（用于裁掉四周空白，好让大小设置准确） */
export function contentBox(canvas) {
  const w = canvas.width;
  const h = canvas.height;
  const ctx = canvas.getContext('2d');
  let d;
  try { d = ctx.getImageData(0, 0, w, h).data; } catch (e) { return { x: 0, y: 0, w: w, h: h }; }
  const rowHas = (y) => {
    for (let x = 0; x < w; x++) if (d[(y * w + x) * 4 + 3] > 8) return true;
    return false;
  };
  const colHas = (x) => {
    for (let y = 0; y < h; y++) if (d[(y * w + x) * 4 + 3] > 8) return true;
    return false;
  };
  let ys = 0, ye = h - 1, xs = 0, xe = w - 1;
  while (ys < ye && !rowHas(ys)) ys++;
  while (ye > ys && !rowHas(ye)) ye--;
  while (xs < xe && !colHas(xs)) xs++;
  while (xe > xs && !colHas(xe)) xe--;
  return { x: xs, y: ys, w: xe - xs + 1, h: ye - ys + 1 };
}

/** 解析任意一种指针文件：.ani / .cur / .ico / png / gif / svg / bmp / jpg */
export async function parseCursorFile(buffer, name) {
  if (/\.ani$/i.test(name || '')) return parseAni(buffer);
  if (/\.(cur|ico)$/i.test(name || '')) {
    const d = await decodeIcon(buffer, 0, buffer.byteLength);
    return { width: d.width, height: d.height, hotspot: d.hotspot, frames: [d.canvas], rates: [60000], steps: [0] };
  }
  const url = URL.createObjectURL(new Blob([buffer]));
  try {
    const img = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => rej(new Error('图片解码失败'));
      im.src = url;
    });
    return {
      width: img.naturalWidth || 32,
      height: img.naturalHeight || 32,
      hotspot: { x: 0, y: 0 },
      frames: [img],
      rates: [60000],
      steps: [0]
    };
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
}

export default { parseAni, parseCursorFile, contentBox };
